/**
 * Sidebar drag-mode effects hook. Extracted verbatim (behavior-preserving) from
 * layout/Sidebar.tsx — bundles the three drag-mode-coupled `useEffect`s that
 * were inline in the orchestrator:
 *
 *   1. The `mousedown` "exit drag mode when clicking outside conversation
 *      items / drag handle / archive button" listener (gated by `isDragMode`).
 *   2. Clearing the search query when the sidebar collapses.
 *   3. Exiting drag mode whenever a non-empty search query is active.
 *
 * Hook-order safety: these were three contiguous `useEffect`s with NOTHING read
 * between them; their dependency arrays move byte-for-byte
 * (`[isDragMode]`, `[isCollapsed]`, `[searchQuery, isDragMode]`). Collapsing
 * them into a single hook call at the same position keeps a stable, identical
 * render-to-render hook order. None of the effects return values; only the first
 * registers/cleans up a listener.
 *
 * The click-target classification is delegated to the `shouldExitDragModeOnClick`
 * kernel (pinned by dragMode.test.ts) so the predicate stays byte-equivalent.
 */

import { useEffect } from 'react';
import { shouldExitDragModeOnClick } from '../dragMode';

interface UseDragModeEffectsArgs {
  isDragMode: boolean;
  setIsDragMode: (value: boolean) => void;
  isCollapsed: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  sidebarRef: React.RefObject<HTMLDivElement | null>;
}

export function useDragModeEffects({
  isDragMode,
  setIsDragMode,
  isCollapsed,
  searchQuery,
  setSearchQuery,
  sidebarRef,
}: UseDragModeEffectsArgs) {
  // Exit drag mode when clicking anywhere except conversation items or archive buttons.
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (shouldExitDragModeOnClick(target, sidebarRef.current)) {
        setIsDragMode(false);
      }
    };

    if (isDragMode) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [isDragMode]);

  useEffect(() => {
    if (isCollapsed) {
      setSearchQuery('');
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (!searchQuery.trim() || !isDragMode) return;
    setIsDragMode(false);
  }, [searchQuery, isDragMode]);
}
